/**
 * EngineDetailSentiment Component
 *
 * Engine-specific detail view for Sentiment Engine.
 * Delegates to the rich SentimentAnalysisView component for the core analysis,
 * then adds the competitive position section.
 */

import { useState } from 'react';
import { Subheading, Text } from '@a7y/tailwind-catalyst';
import type {
  SentimentEngineSpecificAnalysis,
  SentimentFinalMetrics,
  UnifiedDetailedAnalysis,
} from '@a7y/types';
import { SentimentAnalysisView } from "@/components/audit/sentiment/SentimentAnalysisView";
import { CollapsibleSection } from './CollapsibleSection';

export interface EngineDetailSentimentProps {
  /** Sentiment-specific analysis data */
  analysis: SentimentEngineSpecificAnalysis;
  /** Detailed analysis with score_breakdown */
  detail: UnifiedDetailedAnalysis;
  /** When true, force-expands all collapsible sections (for print/PDF) */
  printMode?: boolean;
  /** Additional CSS classes */
  className?: string;
}

export function EngineDetailSentiment({
  analysis,
  detail,
  printMode,
  className = '',
}: EngineDetailSentimentProps) {
  const { competitive_sentiment } = analysis;
  const sb = detail.score_breakdown;

  const [competitiveOpen, setCompetitiveOpen] = useState(true);

  // Reconstruct scores from detail.score_breakdown
  const gradedScore = sb?.rating?.score ?? 0;
  const reviewVolumeScore = sb?.review_volume?.score ?? 0;
  const sentimentHealthScore = sb?.sentiment_health?.score ?? 0;
  const reviewRecencyScore = sb?.review_recency?.score ?? 0;
  const topicConsistencyScore = sb?.topic_consistency?.score ?? 0;

  // Reconstruct SentimentFinalMetrics-compatible object for SentimentAnalysisView
  const finalMetricsForDisplay: SentimentFinalMetrics = {
    overall_analysis: {
      ...analysis.overall_analysis,
      sentiment_health_score: sentimentHealthScore,
    },
    reviews_analysis: analysis.reviews_analysis
      ? {
          ...analysis.reviews_analysis,
          review_recency_score: reviewRecencyScore,
          topic_consistency_score: topicConsistencyScore,
        }
      : null,
    sentiment_analysis: analysis.sentiment_analysis,
    rating_metrics: {
      ...analysis.rating_metrics,
      graded_score: gradedScore,
      review_volume_score: reviewVolumeScore,
    },
    // A7Y-1091 — canonical analyzed-review count, plus its numeric mirror.
    analyzed_review_count: analysis.analyzed_review_count,
    analyzed_review_count_raw: analysis.analyzed_review_count_raw,
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Core Sentiment Analysis (each sub-card has its own CollapsibleSection) */}
      <SentimentAnalysisView finalMetrics={finalMetricsForDisplay} bare printMode={printMode} />

      {/* Competitive Sentiment (not shown by SentimentAnalysisView) */}
      {competitive_sentiment && (
        <CollapsibleSection
          title="Competitive Position"
          isExpanded={competitiveOpen}
          onToggle={() => setCompetitiveOpen((v) => !v)}
          headerColor="gray"
          forceExpanded={printMode}
        >
          <div className="flex items-center justify-between">
            <Text>Category Average Rating</Text>
            <span className="font-medium text-zinc-950 dark:text-white">
              {competitive_sentiment.category_average_rating?.toFixed(1)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <Text>Your Position</Text>
            <span
              className={`font-medium ${
                competitive_sentiment.rating_vs_category > 0
                  ? 'text-green-600 dark:text-green-400'
                  : competitive_sentiment.rating_vs_category < 0
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-zinc-600 dark:text-zinc-400'
              }`}
            >
              {competitive_sentiment.rating_vs_category > 0 ? '+' : ''}
              {competitive_sentiment.rating_vs_category?.toFixed(2)} vs category
            </span>
          </div>

          {/* Competitive Insights */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-zinc-950/10 dark:border-white/10">
            {competitive_sentiment.advantages && competitive_sentiment.advantages.length > 0 && (
              <div>
                <Subheading level={5} className="text-green-600 dark:text-green-400 mb-2">
                  Advantages
                </Subheading>
                <ul className="space-y-1">
                  {competitive_sentiment.advantages.map((adv, i) => (
                    <li
                      key={i}
                      className="text-sm text-zinc-700 dark:text-zinc-200 flex items-start gap-2"
                    >
                      <span className="text-green-400">+</span>
                      <span>{adv}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {competitive_sentiment.weaknesses && competitive_sentiment.weaknesses.length > 0 && (
              <div>
                <Subheading level={5} className="text-yellow-600 dark:text-yellow-400 mb-2">
                  Weaknesses
                </Subheading>
                <ul className="space-y-1">
                  {competitive_sentiment.weaknesses.map((weak, i) => (
                    <li
                      key={i}
                      className="text-sm text-zinc-700 dark:text-zinc-200 flex items-start gap-2"
                    >
                      <span className="text-yellow-400">!</span>
                      <span>{weak}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {competitive_sentiment.complaints && competitive_sentiment.complaints.length > 0 && (
              <div>
                <Subheading level={5} className="text-red-600 dark:text-red-400 mb-2">
                  Complaints
                </Subheading>
                <ul className="space-y-1">
                  {competitive_sentiment.complaints.map((complaint, i) => (
                    <li
                      key={i}
                      className="text-sm text-zinc-700 dark:text-zinc-200 flex items-start gap-2"
                    >
                      <span className="text-red-400">-</span>
                      <span>{complaint}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
}
